import type { OrderStatus } from '~/types/orders.ts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useConfirmOrder, useShipOrder } from '~/api';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { CircleCheckBig, Package, Pen, Truck } from 'lucide-react';
import { ShippingInfo } from '~/pages/order-view/sections/ShippingInfo';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui';

export function SellerOrderCallToAction({
  orderId,
  orderStatus,
}: {
  orderId: string;
  orderStatus: OrderStatus;
}) {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const { mutate: confirmOrder, isPending: isConfirmingOrder } =
    useConfirmOrder();
  const { mutate: shipOrder, isPending: isShippingOrder } = useShipOrder();
  const [showShippingModal, setShowShippingModal] = useState(false);

  if (!['PENDING_SELLER_CONFIRMATION', 'CONFIRMED'].includes(orderStatus)) {
    return null;
  }

  const handleConfirmOrder = () => {
    confirmOrder(
      {
        orderId: orderId!,
      },
      {
        onSuccess: () => {
          toast.success(t('order.confirmSuccess'));
          navigate(`/orders/view/${orderId}`);
        },
      }
    );
  };

  const handleShipOrder = (
    shipDate: Date,
    shipStartDate: Date,
    shipEndDate: Date | null
  ) => {
    shipOrder(
      {
        orderId: orderId!,
        deliveryStartDate: shipStartDate.toISOString(),
        shippedDate: shipDate.toISOString(),
        deliveryEndDate: shipEndDate ? shipEndDate.toISOString() : undefined,
      },
      {
        onSuccess: () => {
          toast.success(t('order.ship_success'));
          navigate(`/orders/view/${orderId}`);
        },
      }
    );
  };

  return (
    <div>
      <Modal
        isOpen={showShippingModal}
        onClose={() => setShowShippingModal(false)}
      >
        <ShippingInfo
          onConfirm={handleShipOrder}
          onCancel={() => setShowShippingModal(false)}
        />
      </Modal>
      {orderStatus === 'PENDING_SELLER_CONFIRMATION' && (
        <>
          <Button onClick={handleConfirmOrder} disabled={isConfirmingOrder}>
            <CircleCheckBig size={18} />
            {t('order.confirm_order')}
          </Button>
          <Button>
            <Pen size="18" />
            {t('order.modify_price')}
          </Button>
          <Button>
            <Package size={18} />
            {t('order.modify_quantity')}
          </Button>
        </>
      )}
      {orderStatus === 'CONFIRMED' && (
        <Button
          onClick={() => setShowShippingModal(true)}
          disabled={isShippingOrder}
        >
          <Truck size={18} />
          {t('order.ship_order')}
        </Button>
      )}
    </div>
  );
}
